import { ref } from 'vue'

export interface Toast {
  id: string
  message: string
  type: 'success' | 'error' | 'info'
  duration?: number
  isVisible: boolean
  buttons?: {
    text: string
    variant?: 'default' | 'reversed' | 'danger' | 'remove'
    onClick: () => void
    isDisabled?: boolean
  }[]
  showClose?: boolean
  icon?: {
    content: string
    background?: string
  }
}

const toasts = ref<Toast[]>([])
let nextId = 1

export const useToast = () => {
  const addToast = (
    message: string,
    type: Toast['type'] = 'info',
    duration: number = 3000,
    options: Partial<Omit<Toast, 'id' | 'message' | 'type' | 'duration' | 'isVisible'>> = {},
  ) => {
    const id = `toast-${nextId++}`

    // Default icons for each type
    const defaultIcon = {
      success: { content: '✓', background: '#8E4EC6' }, // Purple background
      error: undefined, // No icon for error toasts
      info: { content: 'ℹ', background: '#3B82F6' },
    }

    const toast: Toast = {
      id,
      message,
      type,
      duration,
      isVisible: true,
      icon: options.icon || defaultIcon[type],
      ...options,
    }

    toasts.value.push(toast)

    if (duration > 0 && !toast.buttons?.length) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }

    return id
  }

  const removeToast = (id: string) => {
    const index = toasts.value.findIndex((toast) => toast.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }

  const success = (
    message: string,
    duration?: number,
    options?: Partial<Omit<Toast, 'id' | 'message' | 'type' | 'duration' | 'isVisible'>>,
  ) => addToast(message, 'success', duration, options)

  const error = (
    message: string,
    duration?: number,
    options?: Partial<Omit<Toast, 'id' | 'message' | 'type' | 'duration' | 'isVisible'>>,
  ) => addToast(message, 'error', duration, options)

  const info = (
    message: string,
    duration?: number,
    options?: Partial<Omit<Toast, 'id' | 'message' | 'type' | 'duration' | 'isVisible'>>,
  ) => addToast(message, 'info', duration, options)

  return {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    info,
  }
}
